import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrgPaths } from "@/hooks/useOrgPaths";
import { useRetainedValue } from "@/hooks/useRetainedValue";
import { formatTimestampDate, timestampSortKey } from "@/lib/utils";
import { Task } from "@/types/task";
import { BadgeCheck, CalendarClock, Clock, Copy, Edit, MoreHorizontal, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import EditTaskForm from "./EditTaskForm";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";

interface TaskListProps {
  tasks: Task[];
  trialId: string;
  experimentId: string;
  onDeleteTask?: (task: Task) => void;
  onEditTask?: (task: Task) => void;
  /** Narrow state-only update — sends just { state }, never the full task. */
  onChangeTaskState?: (taskId: string, newState: Task["state"]) => void;
  onDuplicateTask?: (task: Task) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

type TaskState = "completed" | "in-progress" | "planned";

const TaskList: React.FC<TaskListProps> = ({ tasks, trialId, experimentId, onDeleteTask, onEditTask, onChangeTaskState, onDuplicateTask, canEdit = false, canDelete = false }) => {
  const paths = useOrgPaths();
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);
  const [taskEditSession, setTaskEditSession] = useState(0);
  const [isEditTaskDirty, setIsEditTaskDirty] = useState(false);
  // Warn before discarding in-progress task edits (Escape, click-outside, X, Cancel).
  const editTaskGuard = useUnsavedChangesGuard({
    isDirty: isEditTaskDirty,
    onOpenChange: (open) => {
      if (!open) setTaskToEdit(null);
    },
  });
  const [openStateDropdown, setOpenStateDropdown] = useState<string | null>(null);
  const renderedTaskToDelete = useRetainedValue(taskToDelete);
  const renderedTaskToEdit = useRetainedValue(taskToEdit);
  const preventCardNavigation = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <BadgeCheck className="h-5 w-5 text-green-600" />;
      case "in-progress":
        return <Clock className="h-5 w-5 text-amber-500" />;
      case "planned":
        return <CalendarClock className="h-5 w-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-50 text-green-700 border-green-200";
      case "in-progress":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "planned":
        return "bg-blue-50 text-blue-700 border-blue-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const handleEditClick = (e: React.MouseEvent, task: Task) => {
    e.stopPropagation();
    setTaskEditSession((current) => current + 1);
    setTaskToEdit(task);
  };

  const handleDeleteClick = (e: React.MouseEvent, task: Task) => {
    e.stopPropagation();
    setTaskToDelete(task);
  };

  const handleDuplicateClick = (e: React.MouseEvent, task: Task) => {
    e.preventDefault();
    e.stopPropagation();
    onDuplicateTask?.(task);
  };

  const confirmDelete = () => {
    if (renderedTaskToDelete && onDeleteTask) {
      onDeleteTask(renderedTaskToDelete);
      // Don't manually set taskToDelete to null here.
      // Let Radix's AlertDialogAction handle the close via onOpenChange,
      // which ensures proper cleanup of body pointer-events.
    }
  };

  const handleEditSubmit = (updatedTask: Task) => {
    if (onEditTask) {
      onEditTask(updatedTask);
      setTaskToEdit(null);
    }
  };

  const handleStateChange = (e: React.MouseEvent, task: Task, newState: TaskState) => {
    e.stopPropagation();
    // Partial state update only — never re-send the (possibly stale) full task,
    // which would rewrite dataCollectionConfigs and could silently strand
    // (hide) a concurrently-added trait's collected data.
    if (onChangeTaskState && task.id && task.state !== newState) {
      onChangeTaskState(task.id, newState);
    }
    setOpenStateDropdown(null);
  };

  // Sort by startDate ascending. A task missing startDate sorts LAST — `?? 0`
  // would float undated tasks to the top and read as a different bug. This runs
  // before any render, so an unguarded call takes the whole list down, not a row.
  const sortedTasks = [...tasks].sort((a, b) => {
    return timestampSortKey(a.startDate) - timestampSortKey(b.startDate);
  });

  return (
    <>
      <div className="grid gap-4">
        {sortedTasks.map((task) => (
          <Link
            key={task.id}
            to={paths.task(trialId, experimentId, task.id)}
            className="block">
            <div className="border rounded-lg p-4 hover:shadow-md transition-shadow relative">
              <div className="flex justify-between items-start">
                <div className="pr-10">
                  <h3 className="font-medium text-lg text-agri-green-dark">{task.name}</h3>
                  <p className="text-muted-foreground text-sm mt-1">{task.description}</p>
                  <p className="text-muted-foreground text-sm mt-2">Date: {formatTimestampDate(task.startDate)}</p>
                  <div className="flex items-center mt-2">
                    {getStatusIcon(task.state)}
                    {canEdit ? (
                      <DropdownMenu open={openStateDropdown === task.id} onOpenChange={(open) => setOpenStateDropdown(open ? task.id : null)}>
                        <DropdownMenuTrigger asChild>
                          <button
                            onClick={preventCardNavigation}
                            className={`text-xs ml-1 px-2 py-1 rounded-full cursor-pointer hover:shadow-md hover:scale-105 transition-all border ${getStatusClass(task.state)}`}
                          >
                            {task.state === "in-progress" ? "In Progress" : task.state.charAt(0).toUpperCase() + task.state.slice(1)}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem
                            onClick={(e) => handleStateChange(e, task, "in-progress")}
                            className="cursor-pointer"
                          >
                            <Clock className="h-4 w-4 mr-2 text-amber-500" />
                            In Progress
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => handleStateChange(e, task, "completed")}
                            className="cursor-pointer"
                          >
                            <BadgeCheck className="h-4 w-4 mr-2 text-green-600" />
                            Completed
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <span className={`text-xs ml-1 px-2 py-1 rounded-full border ${getStatusClass(task.state)}`}>
                        {task.state === "in-progress" ? "In Progress" : task.state.charAt(0).toUpperCase() + task.state.slice(1)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div
                  className="absolute top-4 right-4 flex items-center gap-1"
                  onClick={preventCardNavigation}>
                  {canEdit && onDuplicateTask && (
                    <button
                      onClick={(e) => handleDuplicateClick(e, task)}
                      className="h-8 w-8 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted focus:outline-none"
                      title="Duplicate"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        onClick={preventCardNavigation}
                        className="h-8 w-8 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted focus:outline-none">
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-32">
                      {canEdit && (
                        <DropdownMenuItem
                          onClick={(e) => handleEditClick(e, task)}
                          className="cursor-pointer">
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => handleDeleteClick(e, task)}
                            className="text-destructive focus:text-destructive cursor-pointer">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Delete Task Confirmation */}
      <AlertDialog
        open={taskToDelete !== null}
        onOpenChange={(open) => !open && setTaskToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{renderedTaskToDelete?.name}"? All data associated with this task will be
              permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Task Dialog */}
      <Dialog
        open={!!taskToEdit}
        onOpenChange={editTaskGuard.guardedOnOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Edit Task</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 px-6 pb-6">
            {renderedTaskToEdit && (
              <EditTaskForm
                key={`${renderedTaskToEdit.id}-${taskEditSession}`}
                // Use the LIVE task from the (onSnapshot) list, not the snapshot
                // captured at edit-click — so a trait another client appends
                // while the dialog is open is preserved on save and its data
                // isn't silently stranded (hidden). Fall back to the retained
                // value during close.
                task={tasks.find((t) => t.id === renderedTaskToEdit.id) ?? renderedTaskToEdit}
                onSubmit={handleEditSubmit}
                onCancel={editTaskGuard.requestClose}
                onDirtyChange={setIsEditTaskDirty}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {editTaskGuard.confirmation}
    </>
  );
};

export default TaskList;
